import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Users, UserPlus, Upload, Download, Settings, LogOut, Menu, X, Home, BarChart3, ShieldCheck, KeyRound, Building, GraduationCap, PenTool, Scale, BookOpen, Wallet, Gift, Mail, Calendar, FileCheck, MessageSquare, Bot, Tag, MapPin, Bell, BellOff, Brain, CalendarClock, CalendarCheck, Stamp, Activity, Backpack } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// URL del sonido de notificación (usando un sonido corto y profesional)
const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

const menuItems = [
  { type: 'divider', label: 'Comunicación' },
  { path: '/admin/whatsapp', label: 'Ayni', icon: MessageSquare },
  { path: '/admin/mensajes', label: 'Mensajería Interna', icon: Mail },
  { path: '/admin/bandeja-unificada', label: 'Bandeja Unificada', icon: Mail },
  { path: '/admin/google-groups', label: 'Grupo de Difusión', icon: Users },
  { type: 'divider', label: 'Gestión de Matriculades' },
  { path: '/admin/dashboard', label: 'Matriculades', icon: Users },
  { path: '/admin/consulta-deuda', label: 'Consulta Deuda CATSPBA', icon: Wallet },
  { path: '/admin/estadisticas', label: 'Estadísticas', icon: BarChart3 },
  { type: 'divider', label: 'Mesa Distrital' },
  { path: '/admin/certificados-tramite', label: 'Gestión de Certificados', icon: FileCheck },
  { path: '/admin/calendario-interno', label: 'Calendario CD', icon: CalendarClock },
  { type: 'divider', label: 'Servicios a Matriculades' },
  { path: '/admin/libritos', label: 'Gestión de Libros', icon: BookOpen },
  // iter206 · Entrada unificada: Biblioteca "Laura Iglesias" también contiene
  // las Reservas de Espacio (antes `/admin/reservas` como entrada propia del
  // sidebar). La ruta vieja se mantiene por retrocompatibilidad con deep-links
  // y notificaciones; redirecciona al mismo panel vía la pestaña default.
  { path: '/admin/biblioteca', label: 'Biblioteca "Laura Iglesias"', icon: BookOpen },
  { path: '/admin/auxiliares-justicia', label: 'Auxiliares de Justicia', icon: Scale },
  { path: '/admin/beneficios', label: 'Guía de Beneficios', icon: Gift },
  { path: '/admin/programa-escolar-2027', label: 'Programa Escolar 2027', icon: Backpack },
  { path: '/admin/sellos', label: 'Gestión de Sellos', icon: Stamp },
  { type: 'divider', label: 'Campus y Capacitaciones' },
  { path: '/admin/campus', label: 'Campus Virtual', icon: GraduationCap },
  { path: '/admin/campus/usuarios-externos', label: 'Usuarios Externos', icon: UserPlus },
  { path: '/admin/capacitaciones/cursos', label: 'Gestión de Cursos', icon: BookOpen },
  { path: '/admin/capacitaciones/firmas', label: 'Firmas Certificados', icon: PenTool },
  { path: '/admin/eventos', label: 'Gestión de Eventos', icon: Calendar },
  { type: 'divider', label: 'Recursero' },
  { path: '/admin/instituciones', label: 'Instituciones', icon: Building },
  { path: '/admin/etiquetas-recursero', label: 'Etiquetas/Filtros', icon: Tag },
  { type: 'divider', label: 'Sistema' },
  { path: '/admin/email-templates', label: 'Templates de Email', icon: Mail },
  { path: '/admin/configuracion', label: 'Configuración', icon: Settings },
  { path: '/admin/recursos', label: 'Monitor de Recursos', icon: Activity },
];

// Función para reproducir sonido de notificación
function playNotificationSound() {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0.5;
    audio.play().catch(e => console.log('[SOUND] No se pudo reproducir:', e));
  } catch (e) {
    console.log('[SOUND] Error:', e);
  }
}

// Función para enviar notificación del navegador
function enviarNotificacion(titulo, mensaje, onClick, conSonido = true) {
  if (!("Notification" in window)) {
    console.log("[NOTIF] Este navegador no soporta notificaciones");
    return;
  }

  console.log("[NOTIF] Enviando:", titulo);

  if (Notification.permission === "granted") {
    try {
      const notification = new Notification(titulo, {
        body: mensaje,
        icon: "/logo-colegio.png",
        badge: "/favicon.ico",
        tag: `whatsapp-${Date.now()}`,
        requireInteraction: true,
        silent: false,
      });
      
      notification.onclick = () => {
        window.focus();
        if (onClick) onClick();
        notification.close();
      };
      
      // Auto-cerrar después de 30 segundos
      setTimeout(() => notification.close(), 30000);
      
      // Reproducir sonido
      if (conSonido) {
        playNotificationSound();
      }
      
      console.log("[NOTIF] Notificación enviada exitosamente");
    } catch (e) {
      console.error("[NOTIF] Error enviando notificación:", e);
    }
  }
}

// Solicitar permisos de notificación
async function solicitarPermisosNotificacion() {
  if (!("Notification" in window)) return false;
  
  if (Notification.permission === "granted") return true;
  
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  
  return false;
}

export default function AdminLayout() {
  const { user, logout, isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendientesWhatsApp, setPendientesWhatsApp] = useState(0);
  const [pendientesArchivos, setPendientesArchivos] = useState(0);
  const [demoradasWhatsApp, setDemoradasWhatsApp] = useState(0);
  const [pendientesIA, setPendientesIA] = useState(0);
  const [pendientesMensajes, setPendientesMensajes] = useState(0);
  const [pendientesReservas, setPendientesReservas] = useState(0);
  const [pendientesBandeja, setPendientesBandeja] = useState(0);
  const [pendientesAuxiliares, setPendientesAuxiliares] = useState(0);
  const [notificacionesActivas, setNotificacionesActivas] = useState(false);
  
  // Ref para guardar datos anteriores y detectar cambios
  const ultimosDatosRef = useRef({ 
    solicitudes: [], 
    takeovers: [], 
    archivos: 0,
    mensajes: 0,
    initialized: false 
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
    }
  }, [isAdmin, navigate]);

  // Solicitar permisos de notificación al cargar
  useEffect(() => {
    solicitarPermisosNotificacion().then(granted => {
      setNotificacionesActivas(granted);
      console.log("[NOTIF] Permisos:", granted ? "otorgados" : "denegados");
    });
  }, []);

  // === SSE para badge de Bandeja Unificada (no-leídas) ===
  useEffect(() => {
    if (!token || !isAdmin) return;
    let es;
    try {
      es = new EventSource(`${API_URL}/bandeja-unificada/events?token=${encodeURIComponent(token)}`);
      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          setPendientesBandeja(data.total_no_leidas || 0);
        } catch (e) { /* ignore */ }
      };
      es.onerror = () => {
        // Fallback silencioso: poll una vez a los 60s
        if (es) es.close();
      };
    } catch (e) { /* ignore */ }
    return () => { if (es) es.close(); };
  }, [token, isAdmin]);

  // Función para obtener label del tipo de solicitud
  const getTipoLabel = useCallback((tipo) => {
    const labels = {
      'plan_pago': 'Plan de Pago',
      'plan_de_pago': 'Plan de Pago',
      'certificado_urgente': 'Certificado Urgente',
      'contacto': 'Mensaje',
      'pago_anual': 'Consulta Pago Anual',
      'pago_semestral': 'Consulta Pago Semestral',
      'pago_adelantado': 'Pago Adelantado'
    };
    return labels[tipo] || tipo || 'Solicitud';
  }, []);

  // Polling para obtener notificaciones pendientes de WhatsApp
  useEffect(() => {
    if (!token || !isAdmin) return;

    const fetchPendientes = async () => {
      try {
        const [solRes, tkRes, countersRes, notifRes, mensajesRes, reservasPendRes, auxiliaresPendRes] = await Promise.all([
          axios.get(`${API_URL}/webhooks/twilio/admin/mensajes`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/webhooks/twilio/admin/takeovers/pendientes`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/webhooks/twilio/admin/takeovers/counters`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { alertas: {} } })),
          axios.get(`${API_URL}/webhooks/twilio/admin/notifications`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { notifications: [] } })),
          axios.get(`${API_URL}/mensajes-internos/admin/bandeja-entrada?limit=1`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { respuestas_no_leidas: 0 } })),
          axios.get(`${API_URL}/reservas-espacio/admin/pendientes/count`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { pendientes: 0 } })),
          axios.get(`${API_URL}/admin/auxiliares-justicia/pendientes/count`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { pendientes: 0 } }))
        ]);
        
        // Filtro consistente: pendientes o en conversación activa
        const solicitudesPendientes = solRes.data.filter(m => !m.atendido || m.en_conversacion);
        // Solo contar takeovers que NO son broadcasts (para detectar NUEVOS para sonido)
        const takeoversPendientes = tkRes.data.filter(t => 
          (t.estado === 'pendiente' || t.estado === 'activo') && t.razon !== 'broadcast_proactivo'
        );
        
        // Procesar notificaciones generales
        const archivosNotif = notifRes.data.notifications?.find(n => n.id === 'archivos_pendientes');
        const iaNotif = notifRes.data.notifications?.find(n => n.id === 'ia_correccion');
        
        // Mensajes internos no leídos
        const mensajesNoLeidos = mensajesRes.data.respuestas_no_leidas || 0;
        
        const ultimosDatos = ultimosDatosRef.current;
        
        // Solo enviar notificaciones si ya se inicializó (no en el primer load)
        if (ultimosDatos.initialized && notificacionesActivas) {
          
          // === DETECTAR NUEVOS ARCHIVOS PENDIENTES ===
          if (archivosNotif && archivosNotif.count > ultimosDatos.archivos) {
            const nuevos = archivosNotif.count - ultimosDatos.archivos;
            enviarNotificacion(
              "📎 Nuevos archivos para revisar",
              `${nuevos} archivo(s) pendiente(s) de aprobación`,
              () => navigate('/admin/whatsapp?filtro=archivos'),
              true
            );
          }
          
          // === DETECTAR NUEVAS SOLICITUDES ===
          const idsAnterioresSol = new Set(ultimosDatos.solicitudes.map(s => s.id));
          const nuevasSolicitudes = solicitudesPendientes.filter(s => !idsAnterioresSol.has(s.id));
          
          nuevasSolicitudes.forEach(sol => {
            enviarNotificacion(
              `📋 Nueva Solicitud: ${getTipoLabel(sol.tipo)}`,
              `${sol.phone?.replace('whatsapp:', '') || 'Sin número'}\n${sol.mensaje?.substring(0, 80) || ''}`,
              () => navigate('/admin/whatsapp?filtro=no_leidos')
            );
          });
          
          // === DETECTAR NUEVOS TAKEOVERS ===
          const idsAnterioresTk = new Set(ultimosDatos.takeovers.map(t => t.id));
          const nuevosTakeovers = takeoversPendientes.filter(t => !idsAnterioresTk.has(t.id));
          
          nuevosTakeovers.forEach(tk => {
            enviarNotificacion(
              `👤 Atención Humana Solicitada`,
              `${tk.phone?.replace('whatsapp:', '') || 'Sin número'}\n${tk.trabajador_info || 'Usuario no identificado'}`,
              () => navigate('/admin/whatsapp?filtro=atencion')
            );
          });
          
          // === DETECTAR NUEVOS MENSAJES EN TAKEOVERS ACTIVOS ===
          ultimosDatos.takeovers.forEach(tkAnterior => {
            const tkActual = takeoversPendientes.find(t => t.id === tkAnterior.id);
            if (tkActual && tkActual.mensajes?.length > (tkAnterior.mensajes?.length || 0)) {
              const nuevosMensajes = tkActual.mensajes.slice(tkAnterior.mensajes?.length || 0);
              // Solo notificar mensajes del usuario, no del admin
              nuevosMensajes.filter(m => m.tipo === 'usuario').forEach(msg => {
                enviarNotificacion(
                  `💬 Nuevo mensaje en chat`,
                  `${tkActual.phone?.replace('whatsapp:', '') || ''}\n${msg.mensaje?.substring(0, 80) || ''}`,
                  () => navigate('/admin/whatsapp?filtro=no_leidos')
                );
              });
            }
          });
          
          // === DETECTAR NUEVOS MENSAJES EN SOLICITUDES EN CONVERSACIÓN ===
          ultimosDatos.solicitudes.forEach(solAnterior => {
            const solActual = solicitudesPendientes.find(s => s.id === solAnterior.id);
            if (solActual?.en_conversacion && solActual.historial_chat?.length > (solAnterior.historial_chat?.length || 0)) {
              const nuevosMensajes = solActual.historial_chat.slice(solAnterior.historial_chat?.length || 0);
              nuevosMensajes.filter(m => m.tipo === 'usuario').forEach(msg => {
                enviarNotificacion(
                  `💬 Respuesta en solicitud`,
                  `${solActual.phone?.replace('whatsapp:', '') || ''}\n${msg.mensaje?.substring(0, 80) || ''}`,
                  () => navigate('/admin/whatsapp?filtro=no_leidos')
                );
              });
            }
          });
        }
        
        // Actualizar ref con datos actuales
        ultimosDatosRef.current = {
          solicitudes: solicitudesPendientes,
          takeovers: takeoversPendientes,
          archivos: archivosNotif?.count || 0,
          mensajes: mensajesNoLeidos,
          initialized: true
        };
        
        // Actualizar contadores de badges (fuente de verdad: endpoint /counters)
        const alertas = countersRes.data.alertas || {};
        setPendientesWhatsApp(alertas.sidebar_whatsapp ?? (solicitudesPendientes.length + takeoversPendientes.length));
        setDemoradasWhatsApp(alertas.crm_demoradas || 0);
        // Aviso por demora: una sola notificación por conversación (clave phone|esperando_desde).
        const notificadas = new Set(JSON.parse(localStorage.getItem('ayni_demoras_notificadas') || '[]'));
        (alertas.crm_demoradas_detalle || []).forEach(d => {
          const clave = `${d.phone}|${d.esperando_desde}`;
          if (notificadas.has(clave)) return;
          notificadas.add(clave);
          enviarNotificacion(
            '⏰ Conversación demorada',
            `${d.nombre || d.phone} lleva ${d.demora_min} min esperando atención`,
            () => navigate('/admin/whatsapp?filtro=demoradas')
          );
        });
        localStorage.setItem('ayni_demoras_notificadas', JSON.stringify([...notificadas].slice(-300)));
        setPendientesArchivos(archivosNotif?.count || 0);
        setPendientesIA(iaNotif?.count || 0);
        setPendientesMensajes(mensajesNoLeidos);
        setPendientesReservas(reservasPendRes.data?.pendientes || 0);
        setPendientesAuxiliares(auxiliaresPendRes.data?.pendientes || 0);
        
      } catch (err) {
        console.error('Error fetching WhatsApp pendientes:', err);
      }
    };

    // Fetch inicial
    fetchPendientes();
    
    // Polling cada 8 segundos (más frecuente para mejor UX)
    const interval = setInterval(fetchPendientes, 8000);
    return () => clearInterval(interval);
  }, [token, isAdmin, notificacionesActivas, navigate, getTipoLabel]);

  // Función para toggle de notificaciones
  const toggleNotificaciones = async () => {
    if (notificacionesActivas) {
      // Desactivar notificaciones
      setNotificacionesActivas(false);
      // Reproducir sonido de confirmación
      playNotificationSound();
    } else {
      // Activar notificaciones
      const granted = await solicitarPermisosNotificacion();
      setNotificacionesActivas(granted);
      if (granted) {
        enviarNotificacion(
          "🔔 Notificaciones activadas",
          "Recibirás alertas cuando lleguen nuevas solicitudes o mensajes.",
          null,
          true
        );
      } else {
        alert('Para recibir notificaciones, habilitá los permisos en la configuración del navegador.');
      }
    }
  };

  const handleLogout = () => {
    navigate('/');
    setTimeout(() => {
      logout();
    }, 100);
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 pb-2">
          <img
            src="/logo-colegio.png"
            alt="Colegio de Trabajadorxs Sociales"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h2 className="text-center font-bold text-gray-900 text-sm">Panel de Administración</h2>
        </div>

        {/* User info and actions */}
        <div className="px-4 py-3 mx-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 mb-2 truncate font-medium">{user?.email}</div>
          <div className="flex flex-col gap-1">
            {/* Botón de notificaciones */}
            <Button 
              variant="ghost" 
              size="sm" 
              className={`w-full text-xs justify-start ${
                notificacionesActivas 
                  ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                  : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
              }`}
              onClick={toggleNotificaciones}
              data-testid="admin-notifications-btn"
            >
              {notificacionesActivas ? (
                <>
                  <Bell className="w-3 h-3 mr-1" />
                  Notificaciones ON
                </>
              ) : (
                <>
                  <BellOff className="w-3 h-3 mr-1" />
                  Activar Alertas
                </>
              )}
            </Button>
            <Button variant="ghost" size="sm" className="w-full text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-100 justify-start" onClick={() => navigate('/')} data-testid="admin-home-btn">
              <Home className="w-3 h-3 mr-1" />
              Inicio
            </Button>
            <Button variant="ghost" size="sm" className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50 justify-start" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-3 h-3 mr-1" />
              Cerrar Sesión
            </Button>
          </div>
        </div>

        <nav className="px-4 space-y-0.5 pb-20">
          {menuItems.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={index} className="pt-4 pb-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase px-4">{item.label}</p>
                </div>
              );
            }
            const Icon = item.icon;
            // Match exacto si hay otro ítem del menú cuyo path comienza con este
            const tienePathHijo = menuItems.some(
              (other) => other.path && other.path !== item.path && other.path.startsWith(item.path + '/')
            );
            const isActive = tienePathHijo
              ? (location.pathname === item.path)
              : location.pathname.startsWith(item.path);
            const isWhatsAppMenu = item.path === '/admin/whatsapp';
            const isIATrainingMenu = item.path === '/admin/ia-training';
            const isMensajesMenu = item.path === '/admin/mensajes';
            const isReservasMenu = item.path === '/admin/biblioteca'; // iter206
            const isBandejaMenu = item.path === '/admin/bandeja-unificada';
            const isAuxiliaresMenu = item.path === '/admin/auxiliares-justicia';
            
            // Determinar si mostrar badge y cuál
            let badgeCount = 0;
            let badgeColor = 'bg-red-500';
            if (isWhatsAppMenu && pendientesWhatsApp > 0) {
              badgeCount = pendientesWhatsApp;
              badgeColor = demoradasWhatsApp > 0 ? 'bg-red-600' : 'bg-orange-500';
            } else if (isWhatsAppMenu && pendientesArchivos > 0) {
              badgeCount = pendientesArchivos;
              badgeColor = 'bg-orange-500';
            } else if (isIATrainingMenu && pendientesIA > 0) {
              badgeCount = pendientesIA;
              badgeColor = 'bg-blue-500';
            } else if (isMensajesMenu && pendientesMensajes > 0) {
              badgeCount = pendientesMensajes;
              badgeColor = 'bg-purple-500';
            } else if (isReservasMenu && pendientesReservas > 0) {
              badgeCount = pendientesReservas;
              badgeColor = 'bg-red-500';
            } else if (isBandejaMenu && pendientesBandeja > 0) {
              badgeCount = pendientesBandeja;
              badgeColor = 'bg-red-500';
            } else if (isAuxiliaresMenu && pendientesAuxiliares > 0) {
              badgeCount = pendientesAuxiliares;
              badgeColor = 'bg-amber-500';
            }
            
            return (
              <Link
                key={item.path}
                to={isWhatsAppMenu && badgeCount > 0 ? `${item.path}?filtro=${demoradasWhatsApp > 0 ? 'demoradas' : pendientesWhatsApp > 0 ? 'atencion' : 'archivos'}` : item.path}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors relative ${
                  isActive 
                    ? 'bg-primary text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`menu-${item.path.split('/').pop()}`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
                {badgeCount > 0 && (
                  <span data-testid={`menu-badge-${item.path.split('/').pop()}`} className={`absolute right-3 top-1/2 -translate-y-1/2 ${badgeColor} text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-sm ${isWhatsAppMenu && demoradasWhatsApp > 0 && !isActive ? 'animate-pulse' : ''} ${isActive ? 'ring-2 ring-white' : ''}`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
